import React from 'react';
import { cn } from '@/ui/lib/utils';
import { SavingsIcon } from '@/ui/icons';
import { delimited } from '../../../../common/utils/commonUtil';
import {
  type AngelTax,
  toDisplayAmount,
  toDisplayYen,
} from '../../../models/questions/angelTaxCalculator';

// LP の結果カード（Figma の A_Block）。シミュレーターのページの結果カードより情報を絞り、PC は 3 枚を横に並べる。
// 種別色は angeltax_type3.scss の .angel-tax-card--{tone} が --angel-type に定義したものを使う

interface Props {
  // 優遇措置の種別。種別色を決める
  tone: 'a' | 'b' | 'pre-seed';
  // 優遇措置A など
  title: string;
  angelTax: AngelTax;
}

// 項目名と金額の組。dl の直下に dt と dd を直接包む div を置く（HTML の仕様で許されている形）
function AmountItem({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <div className={cn('tw:flex tw:flex-col tw:gap-1', className)}>
      <dt className="tw:text-base tw:leading-[1.7] tw:font-medium tw:md:font-bold">
        {label}
      </dt>
      <dd className="tw:m-0 tw:flex tw:h-10 tw:items-center tw:justify-end tw:rounded-md tw:bg-muted tw:px-3 tw:text-xl tw:leading-[1.8] tw:font-semibold tw:tracking-text tw:md:font-bold">
        {toDisplayYen(value)}
      </dd>
    </div>
  );
}

export default function ResultCard({ tone, title, angelTax }: Props) {
  return (
    // 上端の見出し（pill）がカードの外へ 22px はみ出すぶん、上の余白を 44px 取る
    <section
      className={cn(
        'tw:relative tw:flex tw:flex-1 tw:flex-col tw:items-center tw:gap-3 tw:rounded-[8px] tw:bg-background tw:px-5 tw:pt-11 tw:pb-5',
        `angel-tax-card--${tone}`,
      )}
    >
      {/* Figma の幅は 256px 固定だが、プレシード・シード特例が収まらないため最小幅として扱う */}
      <h3 className="tw:absolute tw:-top-5.5 tw:left-1/2 tw:m-0 tw:min-w-64 tw:-translate-x-1/2 tw:rounded-full tw:bg-(--angel-type) tw:px-6 tw:py-1 tw:text-center tw:text-xl tw:leading-[1.8] tw:font-bold tw:tracking-text tw:whitespace-nowrap tw:text-primary-foreground">
        {title}
      </h3>

      <dl className="tw:m-0 tw:flex tw:w-full tw:flex-col tw:gap-2">
        <AmountItem label="総所得から控除できる額" value={angelTax.deduction} />
        <AmountItem
          label="エンジェル税制適用時の所得税額"
          value={angelTax.incomeTax}
          className="tw:border-t tw:border-border tw:pt-3"
        />
      </dl>

      {/* Figma の Polygon（種別色を 10% にした下向きの三角形） */}
      <svg
        aria-hidden
        width="84"
        height="21"
        viewBox="0 0 84 20.6038"
        className="tw:shrink-0 tw:text-(--angel-type)"
      >
        <path
          d="M42 20.6038L0 0H84L42 20.6038Z"
          fill="currentColor"
          fillOpacity="0.1"
        />
      </svg>

      {/* 節税効果は SP では白地の枠に金額を大きく出し、PC では見出しを白い pill にして金額を中央に出す */}
      <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-2 tw:rounded-md tw:bg-(--angel-type)/10 tw:p-3 tw:text-(--angel-type) tw:md:gap-3">
        <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:leading-[1.4] tw:font-bold tw:tracking-text tw:md:rounded-full tw:md:bg-background tw:md:px-3 tw:md:py-1">
          <SavingsIcon className="tw:size-4" />
          節税効果
        </div>
        <p className="tw:m-0 tw:flex tw:w-full tw:items-end tw:justify-center tw:gap-1 tw:leading-none tw:md:gap-0">
          <span className="tw:flex-1 tw:bg-background tw:p-2 tw:text-right tw:text-[28px] tw:font-bold tw:text-foreground tw:md:flex-none tw:md:bg-transparent tw:md:p-0 tw:md:text-2xl tw:md:text-inherit">
            {delimited(toDisplayAmount(angelTax.refund))}
          </span>
          <span className="tw:text-xl tw:font-semibold tw:md:text-2xl tw:md:font-bold">
            円
          </span>
        </p>
      </div>
    </section>
  );
}
